import { type FC, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
  TextInput,
} from "../../../../components/ui-kit";
import { Modal } from "../../../../components/modal/Modal";
import {
  useNotification,
  NotificationTypes,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { type PreRegistrationApiType } from "../../../../util/types";
import {
  PaymentsInterimBillingInterval,
  PaymentsPricingMode,
  SubscriptionFeePricingStrategy,
  SubscriptionLevelEnum,
  SubscriptionOptionEnum,
} from "@shared_packages/toolkit/types";
import { getDefaultTrialDays } from "./SubscriptionWizardModal";
import {
  INTERIM_INTERVAL_OPTIONS,
  PRE_REGISTRATION_PRICING_MODE_OPTIONS,
  supportsInterimBilling,
} from "./subscription-wizard.helpers";
import TransactionFeesScheduleEditor from "./TransactionFeesScheduleEditor";
import {
  compactScheduleForSave,
  formatFeeScheduleSummary,
  gbpPaymentRule,
  isCustomFeesWizardMode,
  normalizeSchedule,
  preventNumberInputWheel,
  pricingModeToPaymentsPricingMode,
  scheduleFromLegacyScalars,
  scheduleHasPricedContent,
} from "./subscription-billing.helpers";

// ---------------------------------------------------------------------------
// Styled components (mirrors SubscriptionWizardModal)
// ---------------------------------------------------------------------------

const WizardContainer = styled.div`
  width: 800px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  @media (max-width: 767px) {
    width: 100%;
    max-width: 100%;
    max-height: none;
    padding: 16px;
  }
`;

const WizardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #495b6c;
  line-height: 1;
  padding: 0;
  &:hover {
    color: #13273f;
  }
`;

const StepBarOuter = styled.div`
  position: relative;
  margin: 0 -24px;
  padding: 0 24px;
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 24px;
    z-index: 1;
    pointer-events: none;
  }
  &::before {
    left: 0;
    background: linear-gradient(to right, #fff 30%, transparent);
  }
  &::after {
    right: 0;
    background: linear-gradient(to left, #fff 30%, transparent);
  }
`;

const StepBarScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 4px 0;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StepItem = styled.div<{ $active: boolean; $done: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 0;
  font-size: 11px;
  color: ${({ $active, $done }) =>
    $active ? "#027aff" : $done ? "#2cd19e" : "#bbb"};
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.2s;
`;

const StepDot = styled.div<{ $active: boolean; $done: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $active, $done }) =>
    $done ? "#2cd19e" : $active ? "#027aff" : "#e8e8e8"};
  color: ${({ $active, $done }) => ($done || $active ? "#fff" : "#bbb")};
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
`;

const StepConnector = styled.div<{ $done: boolean }>`
  width: 20px;
  height: 2px;
  background: ${({ $done }) => ($done ? "#2cd19e" : "#e8e8e8")};
  margin: 0 4px;
  flex-shrink: 0;
  transition: background 0.2s;
`;

const OptionGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionCard = styled.div<{ $selected: boolean; $disabled: boolean }>`
  padding: 12px 14px;
  border-radius: 8px;
  border: 2px solid ${({ $selected }) => ($selected ? "#027aff" : "#e0e0e0")};
  background: ${({ $selected }) => ($selected ? "#f0f7ff" : "#fafafa")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: border-color 0.15s, background 0.15s;
  &:hover {
    border-color: ${({ $selected, $disabled }) =>
      $disabled ? "#e0e0e0" : $selected ? "#027aff" : "#9bc8ff"};
  }
`;

const OptionCardTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #13273f;
  margin-bottom: 3px;
`;

const OptionCardDesc = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
`;

const OptionsRow = styled.div`
  display: flex;
  gap: 8px;
  & > * {
    flex: 1;
  }
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ConfigRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ConfigLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #13273f;
`;

const ConfigHint = styled.div`
  font-size: 12px;
  color: #888;
  line-height: 1.4;
`;

const SummaryBox = styled.div`
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
`;

const SummaryKey = styled.span`
  color: #495b6c;
`;
const SummaryValue = styled.span`
  font-weight: 600;
  color: #13273f;
`;

const SummarySectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #aaa;
  padding-top: 6px;
  margin-bottom: 2px;
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
`;

const InfoBanner = styled.div<{ $variant?: "warning" | "info" }>`
  background: ${({ $variant }) =>
    $variant === "warning" ? "#fff8ec" : "#f0f7ff"};
  border: 1px solid
    ${({ $variant }) => ($variant === "warning" ? "#f5c878" : "#9bc8ff")};
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  color: #495b6c;
  line-height: 1.5;
`;

// ---------------------------------------------------------------------------
// Props / Component
// ---------------------------------------------------------------------------

interface PreRegisterModalProps {
  onClose: () => void;
  onSuccess: () => void;
  /** When provided, the modal opens in edit mode for this pre-registration. */
  initialData?: PreRegistrationApiType;
}

const TIERS = [
  SubscriptionLevelEnum.BASIC,
  SubscriptionLevelEnum.STARTER,
  SubscriptionLevelEnum.SCALE_UP,
  SubscriptionLevelEnum.PROFESSIONAL,
  SubscriptionLevelEnum.ENTERPRISE,
];
const TIER_LABELS: Record<string, string> = {
  BASIC: "Basic",
  STARTER: "Starter",
  SCALE_UP: "Scale Up",
  PROFESSIONAL: "Professional",
  ENTERPRISE: "Enterprise",
};

function getSteps(type: SubscriptionOptionEnum, isEditing: boolean): string[] {
  const emailStep = isEditing ? [] : ["Email"];
  if (type === SubscriptionOptionEnum.PAYMENTS_ONLY) {
    return [
      ...emailStep,
      "Subscription type",
      "Pricing mode",
      "Configure",
      "Interim billing",
      "Trial & Discount",
      "Review",
    ];
  }
  return [
    ...emailStep,
    "Subscription type",
    "Tier & billing",
    "Trial & Discount",
    "Review",
  ];
}

function deriveMode(d: PreRegistrationApiType): PaymentsPricingMode {
  if (d.hasBespokePricing) return PaymentsPricingMode.BESPOKE;
  return PaymentsPricingMode.RULE_BASED;
}

function initialPreRegFeeSchedule(d?: PreRegistrationApiType | null) {
  const existing = d?.feeSchedules;
  if (existing?.length) {
    return normalizeSchedule(existing);
  }
  return scheduleFromLegacyScalars({
    customFlatFeePerTransaction: d?.customFlatFeePerTransaction,
    customPercentageFee: d?.customPercentageFee,
    customPercentageFeeMinimum: d?.customPercentageFeeMinimum,
  });
}

const PreRegisterModal: FC<PreRegisterModalProps> = ({
  onClose,
  onSuccess,
  initialData,
}) => {
  const isEditing = !!initialData;

  const [subscriptionType, setSubscriptionType] =
    useState<SubscriptionOptionEnum>(
      initialData?.subscriptionOption ?? SubscriptionOptionEnum.PAYMENTS_ONLY
    );
  const [selectedLevel, setSelectedLevel] = useState<SubscriptionLevelEnum>(
    initialData?.subscriptionLevel ?? SubscriptionLevelEnum.STARTER
  );
  const [billingInterval, setBillingInterval] = useState<"MONTH" | "YEAR">(
    (initialData?.subscriptionInterval as "MONTH" | "YEAR") ?? "MONTH"
  );
  const isToolkit =
    subscriptionType === SubscriptionOptionEnum.TOOLKIT ||
    subscriptionType === SubscriptionOptionEnum.TOOLKIT_MOBILE;
  const steps = getSteps(subscriptionType, isEditing);
  const [stepIdx, setStepIdx] = useState(0);
  const currentStepLabel = steps[stepIdx];
  const goNext = () => setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));

  const [email, setEmail] = useState(initialData?.email ?? "");
  const [selectedMode, setSelectedMode] = useState<PaymentsPricingMode>(
    initialData ? deriveMode(initialData) : PaymentsPricingMode.RULE_BASED
  );
  const [feeSchedule, setFeeSchedule] = useState(() =>
    initialPreRegFeeSchedule(initialData)
  );
  const [interimInterval, setInterimInterval] =
    useState<PaymentsInterimBillingInterval | null>(
      initialData?.paymentsInterimBillingInterval ?? null
    );
  const [trialMode, setTrialMode] = useState<"default" | "custom" | "none">(
    (initialData?.trialPeriodMode as "default" | "custom" | "none") ?? "default"
  );
  const [trialDays, setTrialDays] = useState(
    initialData?.trialPeriodDays != null
      ? String(initialData.trialPeriodDays)
      : ""
  );
  const [hasDiscount, setHasDiscount] = useState(
    initialData?.discountPercentOff != null &&
      initialData.discountPercentOff > 0
  );
  const [discountPercent, setDiscountPercent] = useState(
    initialData?.discountPercentOff != null
      ? String(initialData.discountPercentOff)
      : ""
  );
  const [discountDuration, setDiscountDuration] = useState<
    "once" | "forever" | "repeating"
  >(
    (initialData?.discountDuration as "once" | "forever" | "repeating") ??
      "once"
  );
  const [discountMonths, setDiscountMonths] = useState(
    initialData?.discountDurationInMonths != null
      ? String(initialData.discountDurationInMonths)
      : ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const { showNotification } = useNotification();

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const step3Valid = () => {
    if (isCustomFeesWizardMode(selectedMode)) {
      return scheduleHasPricedContent(feeSchedule);
    }
    return true;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const isCustom = isCustomFeesWizardMode(selectedMode);
      const compactedRaw = isCustom
        ? compactScheduleForSave(feeSchedule)
        : null;
      const compacted =
        compactedRaw && compactedRaw.length ? compactedRaw : null;
      const gbpRule = compacted ? gbpPaymentRule(compacted) : undefined;
      const gbpSchedule = compacted?.find(
        (schedule) => schedule.currency === "gbp"
      );
      const usesHighRiskTier = compacted?.some((schedule) =>
        schedule.rules.some(
          (rule) =>
            rule.pricingStrategy ===
            SubscriptionFeePricingStrategy.HIGH_RISK_TIERED
        )
      );

      const body: Record<string, unknown> = {
        ...(!isEditing && { email: email.trim() }),
        subscriptionOption: subscriptionType,
        subscriptionLevel: isToolkit ? selectedLevel : null,
        subscriptionInterval: isToolkit ? billingInterval : null,
        hasHighRiskSubscriptionPricing: !isToolkit && usesHighRiskTier === true,
        hasBespokePricing:
          !isToolkit && selectedMode === PaymentsPricingMode.BESPOKE,
        paymentsPricingMode: !isToolkit
          ? pricingModeToPaymentsPricingMode(selectedMode)
          : null,
        feeSchedules: compacted,
        customFlatFeePerTransaction: isCustom
          ? gbpRule?.flatFeeMinor ?? null
          : null,
        customPercentageFee: isCustom ? gbpRule?.percentageFee ?? null : null,
        customPercentageFeeMinimum: isCustom
          ? gbpSchedule?.minimumMonthlyFeeMinor ?? null
          : null,
        paymentsInterimBillingInterval: supportsInterimBilling(selectedMode)
          ? interimInterval
          : null,
        trialPeriodMode: trialMode,
        trialPeriodDays: trialMode === "custom" ? Number(trialDays) : null,
        discountPercentOff: hasDiscount ? Number(discountPercent) : null,
        discountDuration: hasDiscount ? discountDuration : null,
        discountDurationInMonths:
          hasDiscount && discountDuration === "repeating"
            ? Number(discountMonths)
            : null,
      };

      if (isEditing) {
        await api.updatePreRegistration(initialData._id, body);
        showNotification(
          "Pre-registration updated successfully",
          NotificationTypes.SUCCESS
        );
      } else {
        await api.createPreRegistration(body);
        showNotification(
          "Pre-registration created successfully",
          NotificationTypes.SUCCESS
        );
      }
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const msg =
        (error as Record<string, Record<string, Record<string, string>>>)?.error
          ?.errors?.message ??
        `Failed to ${isEditing ? "update" : "create"} pre-registration`;
      showNotification(msg, NotificationTypes.WARNING);
    } finally {
      setIsSaving(false);
    }
  };

  const modeLabel =
    PRE_REGISTRATION_PRICING_MODE_OPTIONS.find((o) => o.mode === selectedMode)
      ?.label ?? selectedMode;
  const interimLabel =
    INTERIM_INTERVAL_OPTIONS.find((o) => o.value === interimInterval)?.label ??
    "Monthly only";

  const stepBarRef = useRef<HTMLDivElement>(null);

  const scrollToActiveStep = useCallback(() => {
    if (!stepBarRef.current) return;
    const active = stepBarRef.current.querySelector(
      "[data-active='true']"
    ) as HTMLElement;
    if (active) {
      const container = stepBarRef.current;
      const scrollLeft =
        active.offsetLeft - container.clientWidth / 2 + active.clientWidth / 2;
      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToActiveStep();
  }, [stepIdx, scrollToActiveStep]);

  const renderStepBar = () => (
    <StepBarOuter>
      <StepBarScroll ref={stepBarRef}>
        {steps.map((label, i) => {
          const isActive = stepIdx === i;
          const isDone = stepIdx > i;
          return (
            <span key={label} style={{ display: "contents" }}>
              <StepItem
                $active={isActive}
                $done={isDone}
                data-active={isActive}
              >
                <StepDot $active={isActive} $done={isDone}>
                  {isDone ? "✓" : i + 1}
                </StepDot>
                {label}
              </StepItem>
              {i < steps.length - 1 && <StepConnector $done={isDone} />}
            </span>
          );
        })}
      </StepBarScroll>
    </StepBarOuter>
  );

  return (
    <Modal onHide={onClose}>
      <WizardContainer>
        <WizardHeader>
          <Typography variant={TypographyVariant.HEADER3}>
            {isEditing ? "Edit Pre-registration" : "Pre-register Company"}
          </Typography>
          <CloseButton onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </WizardHeader>

        {renderStepBar()}

        {/* ── Step: Email ── */}
        {currentStepLabel === "Email" && (
          <>
            <InfoBanner>
              Enter the email address the customer will use to register. When
              they sign up with this exact email, the settings you configure
              will be automatically applied to their company.
            </InfoBanner>
            <ConfigRow>
              <ConfigLabel>Customer email</ConfigLabel>
              <TextInput
                label="Customer email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
                placeholder="customer@example.com"
                type="email"
              />
            </ConfigRow>
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={!emailValid}
                onClick={goNext}
              >
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Subscription Type ── */}
        {currentStepLabel === "Subscription type" && (
          <>
            <OptionGrid>
              <OptionCard
                $selected={
                  subscriptionType === SubscriptionOptionEnum.PAYMENTS_ONLY
                }
                $disabled={false}
                onClick={() => {
                  setSubscriptionType(SubscriptionOptionEnum.PAYMENTS_ONLY);
                  setStepIdx(stepIdx);
                }}
              >
                <OptionCardTitle>Payments Only</OptionCardTitle>
                <OptionCardDesc>
                  Usage-based metered billing for payment processing.
                </OptionCardDesc>
              </OptionCard>
              <OptionCard
                $selected={subscriptionType === SubscriptionOptionEnum.TOOLKIT}
                $disabled={false}
                onClick={() => {
                  setSubscriptionType(SubscriptionOptionEnum.TOOLKIT);
                  setStepIdx(stepIdx);
                }}
              >
                <OptionCardTitle>Toolkit</OptionCardTitle>
                <OptionCardDesc>
                  Full e-commerce toolkit with tiered plans (Basic →
                  Enterprise).
                </OptionCardDesc>
              </OptionCard>
              <OptionCard
                $selected={
                  subscriptionType === SubscriptionOptionEnum.TOOLKIT_MOBILE
                }
                $disabled={false}
                onClick={() => {
                  setSubscriptionType(SubscriptionOptionEnum.TOOLKIT_MOBILE);
                  setStepIdx(stepIdx);
                }}
              >
                <OptionCardTitle>Toolkit Mobile</OptionCardTitle>
                <OptionCardDesc>Mobile-focused toolkit variant.</OptionCardDesc>
              </OptionCard>
            </OptionGrid>
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={goNext}>
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Tier & Billing (TOOLKIT) ── */}
        {currentStepLabel === "Tier & billing" && (
          <>
            <div
              style={{
                display: "flex",
                background: "#f0f0f0",
                borderRadius: 8,
                padding: 3,
                gap: 2,
                marginBottom: 12,
              }}
            >
              <button
                onClick={() => setBillingInterval("MONTH")}
                style={{
                  flex: 1,
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  background:
                    billingInterval === "MONTH" ? "#fff" : "transparent",
                  color: billingInterval === "MONTH" ? "#027aff" : "#666",
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval("YEAR")}
                style={{
                  flex: 1,
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  background:
                    billingInterval === "YEAR" ? "#fff" : "transparent",
                  color: billingInterval === "YEAR" ? "#027aff" : "#666",
                }}
              >
                Yearly
              </button>
            </div>
            <OptionGrid>
              {TIERS.map((tier) => (
                <OptionCard
                  key={tier}
                  $selected={selectedLevel === tier}
                  $disabled={false}
                  onClick={() => setSelectedLevel(tier)}
                >
                  <OptionCardTitle>{TIER_LABELS[tier]}</OptionCardTitle>
                </OptionCard>
              ))}
            </OptionGrid>
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={goNext}>
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Pricing mode (PAYMENTS_ONLY) ── */}
        {currentStepLabel === "Pricing mode" && (
          <>
            <OptionGrid>
              {PRE_REGISTRATION_PRICING_MODE_OPTIONS.map(
                ({ mode, label, description }) => (
                  <OptionCard
                    key={mode}
                    $selected={selectedMode === mode}
                    $disabled={false}
                    onClick={() => setSelectedMode(mode)}
                  >
                    <OptionCardTitle>{label}</OptionCardTitle>
                    <OptionCardDesc>{description}</OptionCardDesc>
                  </OptionCard>
                )
              )}
            </OptionGrid>
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={goNext}>
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Configure ── */}
        {currentStepLabel === "Configure" && (
          <>
            <Typography variant={TypographyVariant.BODY1}>
              <strong>Mode:</strong> {modeLabel}
            </Typography>
            <ConfigSection>
              {selectedMode === PaymentsPricingMode.BESPOKE && (
                <InfoBanner>
                  Bespoke pricing flag will be set. The customer will see a
                  bespoke banner instead of standard plans.
                </InfoBanner>
              )}

              {isCustomFeesWizardMode(selectedMode) && (
                <TransactionFeesScheduleEditor
                  value={feeSchedule}
                  onChange={setFeeSchedule}
                  pricingMode={selectedMode}
                />
              )}
            </ConfigSection>
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={!step3Valid()}
                onClick={goNext}
              >
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Interim billing ── */}
        {currentStepLabel === "Interim billing" && (
          <>
            <Typography variant={TypographyVariant.BODY1}>
              <strong>Interim billing frequency</strong>
            </Typography>
            {!supportsInterimBilling(selectedMode) ? (
              <InfoBanner>
                {selectedMode === PaymentsPricingMode.BESPOKE
                  ? "Not applicable for Bespoke pricing."
                  : "Tiered pricing uses monthly billing only. Interim charges are not available."}
              </InfoBanner>
            ) : (
              <>
                <InfoBanner>
                  Interim billing issues charges mid-cycle to prevent large
                  unpaid balances from accumulating.
                </InfoBanner>
                <OptionGrid>
                  {INTERIM_INTERVAL_OPTIONS.map(
                    ({ value, label, description }) => (
                      <OptionCard
                        key={String(value)}
                        $selected={interimInterval === value}
                        $disabled={false}
                        onClick={() => setInterimInterval(value)}
                      >
                        <OptionCardTitle>{label}</OptionCardTitle>
                        <OptionCardDesc>{description}</OptionCardDesc>
                      </OptionCard>
                    )
                  )}
                </OptionGrid>
              </>
            )}
            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button variant={ButtonVariant.CONTAINED} onClick={goNext}>
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Trial & Discount ── */}
        {currentStepLabel === "Trial & Discount" && (
          <>
            <ConfigSection>
              <ConfigLabel>Trial period</ConfigLabel>
              <OptionsRow>
                <OptionCard
                  $selected={trialMode === "default"}
                  $disabled={false}
                  onClick={() => setTrialMode("default")}
                >
                  <OptionCardTitle>
                    {`Default (${getDefaultTrialDays(
                      subscriptionType,
                      feeSchedule.some((schedule) =>
                        schedule.rules.some(
                          (rule) =>
                            rule.pricingStrategy ===
                            SubscriptionFeePricingStrategy.HIGH_RISK_TIERED
                        )
                      )
                    )}-day)`}
                  </OptionCardTitle>
                  <OptionCardDesc>
                    {`Standard ${getDefaultTrialDays(
                      subscriptionType,
                      feeSchedule.some((schedule) =>
                        schedule.rules.some(
                          (rule) =>
                            rule.pricingStrategy ===
                            SubscriptionFeePricingStrategy.HIGH_RISK_TIERED
                        )
                      )
                    )}-day free trial before billing begins.`}
                  </OptionCardDesc>
                </OptionCard>
                <OptionCard
                  $selected={trialMode === "custom"}
                  $disabled={false}
                  onClick={() => setTrialMode("custom")}
                >
                  <OptionCardTitle>Custom</OptionCardTitle>
                  <OptionCardDesc>
                    Set a specific number of trial days.
                  </OptionCardDesc>
                </OptionCard>
                <OptionCard
                  $selected={trialMode === "none"}
                  $disabled={false}
                  onClick={() => setTrialMode("none")}
                >
                  <OptionCardTitle>No trial</OptionCardTitle>
                  <OptionCardDesc>
                    Billing starts immediately — no trial period.
                  </OptionCardDesc>
                </OptionCard>
              </OptionsRow>
              {trialMode === "custom" && (
                <ConfigRow>
                  <ConfigLabel>Trial days</ConfigLabel>
                  <TextInput
                    label="Trial days"
                    value={trialDays}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setTrialDays(e.target.value)
                    }
                    placeholder="e.g. 30"
                    type="number"
                    onWheel={preventNumberInputWheel}
                  />
                </ConfigRow>
              )}
            </ConfigSection>

            <ConfigSection>
              <ConfigRow>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#13273f",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={hasDiscount}
                    onChange={(e) => setHasDiscount(e.target.checked)}
                  />
                  Apply a discount coupon
                </label>
              </ConfigRow>

              {hasDiscount && (
                <>
                  <ConfigRow>
                    <ConfigLabel>Discount %</ConfigLabel>
                    <TextInput
                      label="Discount %"
                      value={discountPercent}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setDiscountPercent(e.target.value)
                      }
                      placeholder="e.g. 20"
                      type="number"
                      onWheel={preventNumberInputWheel}
                    />
                    <ConfigHint>Enter a value between 1 and 100.</ConfigHint>
                  </ConfigRow>

                  <ConfigLabel>Discount duration</ConfigLabel>
                  <OptionsRow>
                    <OptionCard
                      $selected={discountDuration === "once"}
                      $disabled={false}
                      onClick={() => setDiscountDuration("once")}
                    >
                      <OptionCardTitle>Once (1 period)</OptionCardTitle>
                      <OptionCardDesc>
                        Discount applies to the first billing period only.
                      </OptionCardDesc>
                    </OptionCard>
                    <OptionCard
                      $selected={discountDuration === "forever"}
                      $disabled={false}
                      onClick={() => setDiscountDuration("forever")}
                    >
                      <OptionCardTitle>Forever</OptionCardTitle>
                      <OptionCardDesc>
                        Discount applies to every billing period indefinitely.
                      </OptionCardDesc>
                    </OptionCard>
                    <OptionCard
                      $selected={discountDuration === "repeating"}
                      $disabled={false}
                      onClick={() => setDiscountDuration("repeating")}
                    >
                      <OptionCardTitle>Repeating</OptionCardTitle>
                      <OptionCardDesc>
                        Discount applies for a set number of months.
                      </OptionCardDesc>
                    </OptionCard>
                  </OptionsRow>

                  {discountDuration === "repeating" && (
                    <ConfigRow>
                      <ConfigLabel>Number of months</ConfigLabel>
                      <TextInput
                        label="Number of months"
                        value={discountMonths}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setDiscountMonths(e.target.value)
                        }
                        placeholder="e.g. 3"
                        type="number"
                        onWheel={preventNumberInputWheel}
                      />
                    </ConfigRow>
                  )}
                </>
              )}
            </ConfigSection>

            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={
                  (trialMode === "custom" &&
                    (trialDays === "" || Number(trialDays) <= 0)) ||
                  (hasDiscount &&
                    (discountPercent === "" ||
                      Number(discountPercent) < 1 ||
                      Number(discountPercent) > 100)) ||
                  (hasDiscount &&
                    discountDuration === "repeating" &&
                    (discountMonths === "" || Number(discountMonths) <= 0))
                }
                onClick={goNext}
              >
                Next →
              </Button>
            </WizardActions>
          </>
        )}

        {/* ── Step: Review ── */}
        {currentStepLabel === "Review" && (
          <>
            <Typography variant={TypographyVariant.BODY1}>
              Review before saving.
            </Typography>
            <SummaryBox>
              <SummarySectionLabel>Customer</SummarySectionLabel>
              <SummaryRow>
                <SummaryKey>Email</SummaryKey>
                <SummaryValue>{email}</SummaryValue>
              </SummaryRow>

              <SummarySectionLabel>Subscription</SummarySectionLabel>
              <SummaryRow>
                <SummaryKey>Type</SummaryKey>
                <SummaryValue>
                  {subscriptionType.replace(/_/g, " ")}
                </SummaryValue>
              </SummaryRow>
              {isToolkit && (
                <>
                  <SummaryRow>
                    <SummaryKey>Tier</SummaryKey>
                    <SummaryValue>
                      {TIER_LABELS[selectedLevel] ?? selectedLevel}
                    </SummaryValue>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryKey>Billing</SummaryKey>
                    <SummaryValue>
                      {billingInterval === "YEAR" ? "Yearly" : "Monthly"}
                    </SummaryValue>
                  </SummaryRow>
                </>
              )}

              {!isToolkit && (
                <>
                  <SummarySectionLabel>Pricing</SummarySectionLabel>
                  <SummaryRow>
                    <SummaryKey>Mode</SummaryKey>
                    <SummaryValue>{modeLabel}</SummaryValue>
                  </SummaryRow>
                </>
              )}

              {!isToolkit && isCustomFeesWizardMode(selectedMode) && (
                <SummaryRow>
                  <SummaryKey>Fees</SummaryKey>
                  <SummaryValue>
                    {formatFeeScheduleSummary(feeSchedule)
                      .split("\n")
                      .map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                  </SummaryValue>
                </SummaryRow>
              )}

              {!isToolkit && supportsInterimBilling(selectedMode) && (
                <>
                  <SummarySectionLabel>Interim billing</SummarySectionLabel>
                  <SummaryRow>
                    <SummaryKey>Charge frequency</SummaryKey>
                    <SummaryValue>{interimLabel}</SummaryValue>
                  </SummaryRow>
                </>
              )}

              <SummarySectionLabel>Trial & Discount</SummarySectionLabel>
              <SummaryRow>
                <SummaryKey>Trial</SummaryKey>
                <SummaryValue>
                  {trialMode === "default"
                    ? `Default (${getDefaultTrialDays(
                        subscriptionType,
                        feeSchedule.some((schedule) =>
                          schedule.rules.some(
                            (rule) =>
                              rule.pricingStrategy ===
                              SubscriptionFeePricingStrategy.HIGH_RISK_TIERED
                          )
                        )
                      )}-day)`
                    : trialMode === "custom"
                    ? `Custom (${trialDays} days)`
                    : "No trial"}
                </SummaryValue>
              </SummaryRow>
              <SummaryRow>
                <SummaryKey>Discount</SummaryKey>
                <SummaryValue>
                  {hasDiscount
                    ? `${discountPercent}% off — ${
                        discountDuration === "repeating"
                          ? `${discountMonths} months`
                          : discountDuration
                      }`
                    : "None"}
                </SummaryValue>
              </SummaryRow>
            </SummaryBox>

            <InfoBanner $variant="warning">
              {isEditing
                ? `These settings will apply when the customer with ${email} registers (or will be reflected immediately if already configured).`
                : `When the customer registers with ${email}, these settings will be automatically applied to their company.`}
            </InfoBanner>

            <WizardActions>
              <Button variant={ButtonVariant.OUTLINED} onClick={goBack}>
                ← Back
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                disabled={isSaving}
                onClick={handleSave}
              >
                {isSaving
                  ? "Saving..."
                  : isEditing
                  ? "Update pre-registration"
                  : "Create pre-registration"}
              </Button>
            </WizardActions>
          </>
        )}
      </WizardContainer>
    </Modal>
  );
};

export default PreRegisterModal;
